import * as React from "react";
import { connect } from "react-redux";

import { uniq, flatten, values } from "lodash";
import { validators } from "not-valid";
import { Button, Modal, Form, Tab, TabProps, InputOnChangeData } from "semantic-ui-react";

import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { RadioButtonQuestion, RadioButton } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { ReferralFormState } from "@teamprevent/common/questionnaires/components/referralForm/models";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import {
    QuestionnairesState,
    AnsweredQuestion,
    AnsweredQuestionWithId,
    QuestionAnswer,
    QuestionnaireTypes
} from "@teamprevent/common/questionnaires/model";
import { updateQuestion, removeQuestion, openSection } from "@teamprevent/common/questionnaires/actions";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { FilesQuestion } from "@teamprevent/common/questionnaires/components/questions/FilesQuestion";
import { QuestionnaireConfig, AdditionalQuestionsSectionId, renderAdditionalQuestions } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { dateOfBirthInRange } from "@teamprevent/common/validators/dateOfBirthInRange";

import * as questionIds from "../../questionnaires/questionnaireConfig/ppqEmployeeSection/questionIds";
import { GENDER_OPTIONS } from "../../utils";
import { calculateBmi as calculateBmiService } from "../../bmi/calculateBmi";
import { GENDER_AT_BIRTH_OPTIONS, ETHINICITY_OPTIONS, ASTHMA_OPTIONS, DIABETES_OPTIONS,
    CHRONIC_KIDNEY_DISEASE_OPTIONS, CANCER_OPTIONS, HEART_OPTIONS, COVID19_VACCINE_OPTIONS } from "../models";

import { PpqCovid19OutcomeSection } from "./PpqCovid19OutcomeSection";
import { PpqCovid19Disclaimer } from "./PpqCovid19Disclaimer";

import { Location, Link } from "redux-little-router";

export const QUESTIONNAIRE_KEY = "PpqEmployeeSection";

interface OwnProps {
    caseId?: string;
    hideEmployeeDetails?: boolean;
    hideDeclaration?: boolean;
    hideDividers?: boolean;
    sectionsOpen?: boolean;
    readonly: boolean;
    submitQuestionnaire?: (answers: AnsweredQuestionWithId[], questionnaireKey: string) => Promise<boolean>;
    questionnaireConfig: QuestionnaireConfig;
    createCreateImmunisationsButton?: (evidenceBlobId: string, evidenceFileName: string) => React.ReactNode;
    hideSubmitButton?: boolean;
    highlightSpecificAnswers?: boolean;
    evidenceApiUrl: string;
}

interface StateProps {
    caseId: string;
    questions: AnsweredQuestionWithId[];
    managersSectionQuestions: AnsweredQuestionWithId[];
}

interface DispatchProps {
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    removeQuestion: (payload: { sectionId: string; id: string }) => void;
    openFirstSection: () => void;
}

interface OwnState {
    isBmiModalOpen: boolean;
    calculatedBmi: string;
    bmiModalActiveIndex: number;
    metricHeight: number;
    metricWeight: number;
    imperialHeightFeet: number;
    imperialHeightInches: number;
    imperialWeightStone: number;
    imperialWeightPounds: number;
}

export const candidateDetailsSectionId = "CandidateDetails";

class PpqEmployeeSectionUnconnected extends React.Component<OwnProps & StateProps & DispatchProps, OwnState> {
    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        this.state = {
            isBmiModalOpen: false,
            calculatedBmi: null,
            bmiModalActiveIndex: 0,
            metricHeight: 1.6,
            metricWeight: 60,
            imperialHeightFeet: 5,
            imperialHeightInches: 0,
            imperialWeightStone: 10,
            imperialWeightPounds: 0
        };
        this.loadQuestionnaire = this.loadQuestionnaire.bind(this);
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.calculateBmi = this.calculateBmi.bind(this);
        this.handleMetricHeightChange = this.handleMetricHeightChange.bind(this);
        this.handleMetricWeightChange = this.handleMetricWeightChange.bind(this);
        this.handleImperialHeightFeetChange = this.handleImperialHeightFeetChange.bind(this);
        this.handleImperialHeightInchesChange = this.handleImperialHeightInchesChange.bind(this);
        this.handleImperialWeightStoneChange = this.handleImperialWeightStoneChange.bind(this);
        this.handleImperialWeightPoundsChange = this.handleImperialWeightPoundsChange.bind(this);
    }

    public componentDidMount() {
        this.props.openFirstSection();
    }

    public render() {
        const bmiModalTabs = [
            {
                menuItem: "Metric",
                render: () => {
                    return (
                        <Tab.Pane attached={false}>
                            <Form.Field>
                                <p><b>Height (m)</b></p>
                                <Form.Input
                                    type="number"
                                    value={this.state.metricHeight}
                                    onChange={this.handleMetricHeightChange}
                                />
                                <p><b>Weight (kg)</b></p>
                                <Form.Input
                                    type="number"
                                    value={this.state.metricWeight}
                                    onChange={this.handleMetricWeightChange}
                                />
                            </Form.Field>
                        </Tab.Pane>
                    );
                }
            },
            {
                menuItem: "Imperial",
                render: () => {
                    return (
                        <Tab.Pane attached={false}>
                            <h4>Height</h4>
                            <Form.Field>
                                <p><b>Feet</b></p>
                                <Form.Input
                                    type="number"
                                    value={this.state.imperialHeightFeet}
                                    onChange={this.handleImperialHeightFeetChange}
                                />
                                <p><b>Inches</b></p>
                                <Form.Input
                                    type="number"
                                    value={this.state.imperialHeightInches}
                                    onChange={this.handleImperialHeightInchesChange}
                                />
                            </Form.Field>
                            <h4>Weight</h4>
                            <Form.Field>
                                <p><b>Stone</b></p>
                                <Form.Input
                                    type="number"
                                    value={this.state.imperialWeightStone}
                                    onChange={this.handleImperialWeightStoneChange}
                                />
                                <p><b>Pounds</b></p>
                                <Form.Input
                                    type="number"
                                    value={this.state.imperialWeightPounds}
                                    onChange={this.handleImperialWeightPoundsChange}
                                />
                            </Form.Field>
                        </Tab.Pane>
                    );
                }
            }
        ];

        /* eslint-disable max-len */
        return (
            <OldProgressBarQuestionnaireContainer
                loadQuestionnaire={this.loadQuestionnaire}
                submitQuestionnaire={this.submitQuestionnaire}
                readonly={this.props.readonly}
                questionnaireKey={QUESTIONNAIRE_KEY}
                hideSubmitButton={this.props.hideSubmitButton}
                questionnaireType={QuestionnaireTypes.Other}
            >
                {this.props.hideEmployeeDetails ||
                    <OldProgressBarQuestionnaireSection title="Please confirm your own details" id={candidateDetailsSectionId} active={this.props.sectionsOpen}>
                        <p>Text messages and email notifications may be sent to you to notify/remind you of any forthcoming appointments.
                            It is important that you provide all the details below. By providing these details you are confirming that you
                    agree to receiving notifications by email, text and post.</p>
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.Title)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                            validators={[validators.validLength({ max: 25, message: "Maximum of 25 characters only" })]}
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.FirstName)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.LastName)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={this.getQuestionConfig(questionIds.Gender)}
                            options={GENDER_OPTIONS}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <OldDateQuestion
                            questionConfig={this.getQuestionConfig(questionIds.Dob)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                            validators={[dateOfBirthInRange]}
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.AddressLine1)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.AddressLine2)}
                            readonly={this.props.readonly}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.AddressLine3)}
                            readonly={this.props.readonly}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.City)}
                            readonly={this.props.readonly}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.County)}
                            readonly={this.props.readonly}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.Postcode)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.MobilePhone)}
                            readonly={this.props.readonly}
                            validators={[validators.validPhoneNumber(10)]}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HomePhone)}
                            readonly={this.props.readonly}
                            validators={[validators.validPhoneNumber(10)]}
                            hideDivider
                        />
                    </OldProgressBarQuestionnaireSection>}
                <OldProgressBarQuestionnaireSection title="General Health" id="GeneralHealth" active={this.props.sectionsOpen}>
                    <RadioButtonQuestion
                        questionConfig={this.getQuestionConfig(questionIds.HasDisability)}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={this.props.hideDividers}
                        answerHighlightCriteria={this.shouldHighlightYesNoAnswer}
                    >
                        <RadioButton label="Yes" value="Yes" />
                        <RadioButton label="No" value="No" />
                    </RadioButtonQuestion>
                    <RadioButtonQuestion
                        questionConfig={this.getQuestionConfig(questionIds.EverHadDisability)}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={this.props.hideDividers}
                        answerHighlightCriteria={this.shouldHighlightYesNoAnswer}
                    >
                        <RadioButton label="Yes" value="Yes" />
                        <RadioButton label="No" value="No" />
                    </RadioButtonQuestion>
                    <RadioButtonQuestion
                        questionConfig={this.getQuestionConfig(questionIds.HavingTreatment)}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={this.props.hideDividers}
                        answerHighlightCriteria={this.shouldHighlightYesNoAnswer}
                    >
                        <RadioButton label="Yes" value="Yes" />
                        <RadioButton label="No" value="No" />
                    </RadioButtonQuestion>
                    <RadioButtonQuestion
                        questionConfig={this.getQuestionConfig(questionIds.HasConditionRequiringAdjustment)}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={this.props.hideDividers}
                        answerHighlightCriteria={this.shouldHighlightYesNoAnswer}
                    >
                        <RadioButton label="Yes" value="Yes" />
                        <RadioButton label="No" value="No" />
                    </RadioButtonQuestion>
                    <TextQuestion
                        questionConfig={this.getQuestionConfig(questionIds.FurtherDetails)}
                        multiline
                        readonly={this.props.readonly}
                        hideDivider
                        answerHighlightCriteria={() => !!this.props.highlightSpecificAnswers}
                    />
                </OldProgressBarQuestionnaireSection>
                {this.getManagersAnswerToQuestionWithId("RegularPatientContact") &&
                    <OldProgressBarQuestionnaireSection title="Health Care Workers" id="HealthCareWorkers" active={this.props.sectionsOpen}>
                        <p>
                            Healthcare Workers must be able to provide documentary evidence of immunity to the following communicable diseases, Measles,
                            Rubella, Tuberculosis and Hepatitis B. Evidence may be in the form of actual blood test results, vaccination cards, GP records
                            or a report from your previous Occupational Health Provider
                        </p>
                        <p>
                            Evidence must be provided before health clearance can be given. If evidence is not available you will be required to attend the Occupational
                            Health and Wellbeing department and we may need to carry out blood tests or start a course of vaccinations which could delay your clearance.
                        </p>
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideMmrVaccineEvidence)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideMmrVaccineEvidence") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.MmrVaccineEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideMeaslesRubellaTestEvidence)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideMeaslesRubellaTestEvidence") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.MeaslesRubellaTestEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideBcgCheckEvidence)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideBcgCheckEvidence") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.BcgCheckEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideVaricellaEvidence)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideVaricellaEvidence") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.VaricellaEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideHepatitisBImmunityEvidence)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideHepatitisBImmunityEvidence") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.HepatitisBImmunityEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadChickenPox)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                            <RadioButton label="Don't know" value="Don't know" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadChildhoodVaccinations)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                            <RadioButton label="Don't know" value="Don't know" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HaveCoughMoreThan3Weeks)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HaveUnexplainedWeightLoss)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HaveUnexplainedFever)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HaveTuberculosis)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.TravelToTbAreas)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.LivedContinuouslyInUk)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.CountriesLivedIn)}
                            readonly={this.props.readonly}
                            multiline
                            hideDivider={this.props.hideDividers}
                        />
                    </OldProgressBarQuestionnaireSection>}
                {this.getManagersAnswerToQuestionWithId("ExposureProneProcedure") &&
                    <OldProgressBarQuestionnaireSection title="EPP Workers" id="EppWorkers" active={this.props.sectionsOpen}>
                        <p>
                            Exposure Prone Procedures are those procedures where the worker's gloved hands may be in full contact with sharp instruments,
                            needle tips or sharp tissue (e.g. spicules of bone or teeth) inside a patient's open body cavity, wound or confined anatomical
                            space where the hands or fingertips may not be completely visible at all times.
                        </p>
                        <p>
                            Staff performing Exposure Prone Procedures must provide validated documentary evidence of their Hepatitis B, Hepatitis C and HIV status
                            before health clearance can be given. If evidence is not available you will be asked to attend the Occupational Health department for
                            serological screening and health clearance will be delayed until these results are processed. You will be asked to show formal photographic
                            ID, e.g. valid driver’s license or passport, for this procedure. Please attach any evidence you have of previous testing to this form
                        </p>
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideHepatitisBStatus)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideHepatitisBStatus") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.HepatitisBStatusEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideHepatitisCStatus)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideHepatitisCStatus") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.HepatitisCStatusEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CanProvideHivStatus)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                                value="I will obtain evidence from my previous Occupational Health Provider and will provide this as soon as possible"
                            />
                            <RadioButton
                                label="I do not have and am unable to obtain any documentary evidence"
                                value="I do not have and am unable to obtain any documentary evidence"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence("CanProvideHivStatus") &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.HivStatusEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />}
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadHepatitisB)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadHepatitisC)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadHiv)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadHighRiskFromHep)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: QUESTIONNAIRE_KEY,
                                sectionId: "EppWorkers"
                            }}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                    </OldProgressBarQuestionnaireSection>}
                {this.getManagersAnswerToQuestionWithId("FoodHandling") &&
                    <OldProgressBarQuestionnaireSection title="Food Handling" id="FoodHandling" active={this.props.sectionsOpen}>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadTyphoid)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadBowelDisorders)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadTuberculosis)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadAsthmaChestInfection)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadRecurringSkinDisorders)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadAllergicReactions)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadBackNeckArmWristProblems)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadDeafnessDefectiveVision)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadBlackoutsDizzySpells)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadRecentSkinInfection)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadRecentDiarrhoeaVomiting)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadRecentEarEyeGumInfection)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.HadTyphoidContact)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.OngoingSymptomsAndTreatments)}
                            readonly={this.props.readonly}
                            multiline
                            hideDivider={this.props.hideDividers}
                        />
                    </OldProgressBarQuestionnaireSection>}
                {this.getManagersAnswerToQuestionWithId("EvidenceOfCovid19VaccinationStatusMandatory") &&
                    <OldProgressBarQuestionnaireSection title="COVID-19 Vaccinations" id="Covid19Vaccinations" active={this.props.sectionsOpen}>
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.CovidVaccinationStatus)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="I have evidence I can upload" value="I have evidence I can upload" />
                            <RadioButton
                                label="Yes, I will send evidence to Occupational Health separately"
                                value="Yes, I will send evidence to Occupational Health separately"
                            />
                            <RadioButton
                                label="No, I have not yet been vaccinated"
                                value="No, I have not yet been vaccinated"
                            />
                            <RadioButton
                                label="My first vaccine is booked"
                                value="My first vaccine is booked"
                            />
                            <RadioButton
                                label="I am exempt from COVID-19 vaccination"
                                value="I am exempt from COVID-19 vaccination"
                            />
                        </RadioButtonQuestion>
                        {this.canSupplyEvidence(questionIds.CovidVaccinationStatus) &&
                            <FilesQuestion
                                api={this.props.evidenceApiUrl}
                                questionConfig={this.getQuestionConfig(questionIds.CovidVaccineEvidence)}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider={this.props.hideDividers}
                                createAdditionalFileItems={this.props.createCreateImmunisationsButton}
                                acceptExtensions={acceptableExtensions}
                            />
                        }
                        {this.getAnswerToQuestionWithId(questionIds.CovidVaccinationStatus) === "My first vaccine is booked" &&
                            <>
                                <OldDateQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.CovidVaccinationDate)}
                                    readonly={this.props.readonly}
                                    mandatory
                                    hideDivider
                                />
                            </>
                        }
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CovidVaccinationEvidenceRequiredConsent)}
                            value="Confirmed"
                            hideDivider={this.props.hideDividers}
                        />
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.CovidVaccinationEvidenceSharingConsent)}
                            value="Confirmed"
                            hideDivider={this.props.hideDividers}
                        />
                    </OldProgressBarQuestionnaireSection>}
                {this.getManagersAnswerToQuestionWithId("Covid19RiskAssessment") &&
                    <OldProgressBarQuestionnaireSection title="COVID-19 Risk Assessment" id="Covid19RiskAssessment" active={this.props.sectionsOpen}>
                        <h4>
                            Statement of Understanding
                        </h4>
                        <p>
                            Optima Health COVID-19 Risk Assessment is a tool that helps assess an individual’s vulnerability to COVID-19.
                            It is based on published evidence of the main risk factors through ALAMA.  More information can be found
                            on the <Link href="https://alama.org.uk/covid-19-medical-risk-assessment/" target="_blank"><u>ALAMA website</u></Link>.
                            The tool summarises vulnerability for combinations of risk factors including age, sex, ethnicity,
                            and various health problems. It works by “translating” the risk associated with each risk factor then
                            gives a single overall measure of vulnerability to infection of either very high, high, moderate, and low.
                            The calculated measure of risk along with the advice to your employer on how to manage this risk will be
                            included in the Pre-placement fitness certificate that we generate for your employer.

                            It is important to recognise that scores generated from this risk prediction model are arbitrary values,
                            not based on absolute risk, the scores are used to determine a category that should be used as a general guide
                            for an employer to take action.
                        </p>
                        <RadioButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.Covid19RiskAssessmentStatementOfUnderstanding)}
                            mandatory
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton
                                label="To proceed with the COVID-19 Risk Assessment please tick to confirm you have read and understand the statement above"
                                value="ProceedWithCovid19RiskAssessment"
                            />
                            <RadioButton
                                label="If you do not wish to complete a COVID-19 Risk Assessment please tick this box to proceed.  Your employer will be notified of your decision not proceed with a risk assessment"
                                value="DoNotProceedWithCovid19RiskAssessment"
                            />
                        </RadioButtonQuestion>
                        {this.getAnswerToQuestionWithId(questionIds.Covid19RiskAssessmentStatementOfUnderstanding) === "ProceedWithCovid19RiskAssessment" &&
                        <>
                            <NumberQuestion
                                hideDivider
                                mandatory
                                readonly={this.props.readonly}
                                min={0}
                                questionConfig={this.getQuestionConfig(questionIds.Age)}
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.GenderAtBirth)}
                                options={GENDER_AT_BIRTH_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.Ethnicity)}
                                options={ETHINICITY_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            {!this.props.readonly &&
                            <>
                                <Button content="Calculate BMI" onClick={() => this.openBmiModal()} />
                                <Modal open={this.state.isBmiModalOpen}>
                                    <Modal.Content>
                                        <Form>
                                            <h3>Please input height and weight:</h3>
                                            <Tab
                                                menu={{ secondary: true, pointing: true }}
                                                panes={bmiModalTabs}
                                                activeIndex={this.state.bmiModalActiveIndex}
                                                onTabChange={this.handleTabChange}
                                            />
                                            <br />
                                            {this.state.calculatedBmi && <p><b>Calculated BMI: {this.state.calculatedBmi}</b></p>}
                                        </Form>
                                    </Modal.Content>
                                    <Modal.Actions>
                                        <Button content="Calculate" onClick={() => this.calculateBmi()} />
                                        <Button content="Exit" onClick={() => this.closeBmiModal()} />
                                    </Modal.Actions>
                                </Modal>
                                <br />
                            </>
                            }
                            <NumberQuestion
                                answer={this.state.calculatedBmi}
                                questionConfig={this.getQuestionConfig(questionIds.Bmi)}
                                readonly={true}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.Asthma)}
                                options={ASTHMA_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.Diabetes)}
                                options={DIABETES_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ChronicKidneyDisease)}
                                options={CHRONIC_KIDNEY_DISEASE_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.CancerOtherThanLeukaemiaLymphomaOrMyeloma)}
                                options={CANCER_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.LeukaemiaLymphomaOrMyeloma)}
                                options={CANCER_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <DropdownQuestion
                                questionConfig={this.getQuestionConfig(questionIds.Heart)}
                                options={HEART_OPTIONS}
                                readonly={this.props.readonly}
                                mandatory
                                hideDivider
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.OtherChronicRespiratorydDisease)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.Hypertension)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.CerebrovascularDisease)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.LiverDisease)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.ChronicNeurologicalDiseaseOtherThanStrokeOrDementia)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.OrganTransplant)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.SpleenDiseases)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.RheumatoidLupusPsoriasis)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <CheckboxQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.OtherImmunosuppresiveCondition)}
                                value="Confirmed"
                                hideDivider={this.props.hideDividers}
                            />
                            <RadioButtonQuestion
                                readonly={this.props.readonly}
                                questionConfig={this.getQuestionConfig(questionIds.Covid19VaccineStatus)}
                                mandatory
                                hideDivider={this.props.hideDividers}
                            >
                                <RadioButton
                                    label="I have not yet been invited for my first COVID-19 vaccination or I have been invited for a COVID-19 vaccination but I have chosen not to book a vaccination appointment"
                                    value="NoCovid19VaccineBookedOrTaken"
                                />
                                <RadioButton
                                    label="I have had my first COVID-19 vaccination or have booked my first vaccination and my new role or placement is due to start in less than 3 weeks from my first vaccination date"
                                    value="FirstCovid19VaccineBookedAndPlacementLessThan3Weeks"
                                />
                                <RadioButton
                                    label="I have had my first COVID-19 vaccination or have booked my first vaccination and my new role or placement is due to start more than 3 weeks from my first vaccination date"
                                    value="FirstCovid19VaccineBookedAndPlacementMoreThan3Weeks"
                                />
                            </RadioButtonQuestion>
                            {(this.getAnswerToQuestionWithId(questionIds.Covid19VaccineStatus) === "FirstCovid19VaccineBookedAndPlacementLessThan3Weeks" ||
                            this.getAnswerToQuestionWithId(questionIds.Covid19VaccineStatus) === "FirstCovid19VaccineBookedAndPlacementMoreThan3Weeks") &&
                            <>
                                <p>
                                As your Occupational Health Provider we are keen to ensure that we hold a record of your COVID-19 vaccination status.
                                If you are  happy for us to store these details on your Occupational Health records please enter the details below.
                                If you are not then please do not answer these questions.
                                </p>
                                <CheckboxQuestion
                                    readonly={this.props.readonly}
                                    questionConfig={this.getQuestionConfig(questionIds.Covid19FirstVaccineStatus)}
                                    value="Confirmed"
                                    hideDivider={this.props.hideDividers}
                                />
                                {this.getAnswerToQuestionWithId(questionIds.Covid19FirstVaccineStatus) === "Confirmed" &&
                                <>
                                    <OldDateQuestion
                                        questionConfig={this.getQuestionConfig(questionIds.Covid19FirstVaccineDate)}
                                        readonly={this.props.readonly}
                                        hideDivider
                                    />
                                    <DropdownQuestion
                                        questionConfig={this.getQuestionConfig(questionIds.Covid19FirstVaccineType)}
                                        options={COVID19_VACCINE_OPTIONS}
                                        readonly={this.props.readonly}
                                        hideDivider
                                    />
                                </>
                                }
                                {this.getAnswerToQuestionWithId(questionIds.Covid19VaccineStatus) === "FirstCovid19VaccineBookedAndPlacementMoreThan3Weeks" &&
                                <>
                                    <CheckboxQuestion
                                        readonly={this.props.readonly}
                                        questionConfig={this.getQuestionConfig(questionIds.Covid19SecondVaccineStatus)}
                                        value="Confirmed"
                                        hideDivider={this.props.hideDividers}
                                    />
                                    {this.getAnswerToQuestionWithId(questionIds.Covid19SecondVaccineStatus) === "Confirmed" &&
                                    <>
                                        <OldDateQuestion
                                            questionConfig={this.getQuestionConfig(questionIds.Covid19SecondVaccineDate)}
                                            readonly={this.props.readonly}
                                            hideDivider
                                        />
                                        <DropdownQuestion
                                            questionConfig={this.getQuestionConfig(questionIds.Covid19SecondVaccineType)}
                                            options={COVID19_VACCINE_OPTIONS}
                                            readonly={this.props.readonly}
                                            hideDivider
                                        />
                                    </>
                                    }
                                </>
                                }
                                <CheckboxQuestion
                                    readonly={this.props.readonly}
                                    questionConfig={this.getQuestionConfig(questionIds.Covid19VaccineDeclaration)}
                                    value="Confirmed"
                                    hideDivider={this.props.hideDividers}
                                />
                            </>
                            }
                        </>}
                    </OldProgressBarQuestionnaireSection>}
                {this.props.readonly &&
                    this.getManagersAnswerToQuestionWithId("Covid19RiskAssessment") &&
                    <OldProgressBarQuestionnaireSection neverHide={true} title="ALAMA Outcome" id="AlamaOutcome" active={this.props.sectionsOpen}>
                        {this.getAnswerToQuestionWithId(questionIds.Covid19RiskAssessmentStatementOfUnderstanding) !== "ProceedWithCovid19RiskAssessment" &&
                            <>
                                <p>The candidate chose not to complete the COVID-19 Risk Assessment</p>
                            </>
                        }
                        {this.getAnswerToQuestionWithId(questionIds.Covid19RiskAssessmentStatementOfUnderstanding) === "ProceedWithCovid19RiskAssessment" &&
                        <>
                            <b>
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.CovidFinalRiskGroup)}
                                    readonly={true}
                                    hideDivider
                                    inline
                                />
                            </b>
                            <PpqCovid19OutcomeSection covidFinalRiskGroup={this.getAnswerToQuestionWithId(questionIds.CovidFinalRiskGroup) as string} />
                            <br />
                            <br />
                            <p><b>COVID-19 Risk Assessment Disclaimer</b></p>
                            <PpqCovid19Disclaimer />
                        </>
                        }
                    </OldProgressBarQuestionnaireSection>}
                <OldProgressBarQuestionnaireSection id={AdditionalQuestionsSectionId} title="Additional Questions">
                    {renderAdditionalQuestions(this.props.questionnaireConfig.additionalQuestions, this.props.readonly, false)}
                </OldProgressBarQuestionnaireSection>
                {this.props.hideDeclaration ||
                    <OldProgressBarQuestionnaireSection title="Declaration" id="Declaration">
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.PrivacyStatementConfirmation)}
                            value="Confirmed"
                            mandatory
                            hideDivider={this.props.hideDividers}
                        />
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.TeamPreventProcessPersonalInfoConfirmation)}
                            value="Confirmed"
                            mandatory
                            hideDivider={this.props.hideDividers}
                        />
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.InformationHeldSecurelyConfirmation)}
                            value="Confirmed"
                            mandatory
                            hideDivider={this.props.hideDividers}
                        />
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.FitnessToWorkProvidedToEmployerConfirmation)}
                            value="Confirmed"
                            mandatory
                            hideDivider={this.props.hideDividers}
                        />
                        <CheckboxQuestion
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.AnswersTrueConfirmation)}
                            value="Confirmed"
                            mandatory
                            hideDivider={this.props.hideDividers}
                        />
                    </OldProgressBarQuestionnaireSection>}
            </OldProgressBarQuestionnaireContainer>);
        /* eslint-enable max-len */
    }

    // The loading of this questionnaire is controlled by the parent component as the question config array might come from various places.
    // eslint-disable-next-line no-empty
    public loadQuestionnaire() {
        // Do nothing
    }

    public async submitQuestionnaire(answers: AnsweredQuestionWithId[]): Promise<boolean> {

        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.Title, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.FirstName, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.LastName, answers);

        await this.props.submitQuestionnaire(answers, QUESTIONNAIRE_KEY);

        return true;
    }

    private trimAnswerAndUpdateEmployeeQuestion(questionId: string, questions: AnsweredQuestionWithId[]) {

        const stateQuestion = this.props.questions.find(q => q.id === questionId);

        if (stateQuestion) {
            this.props.updateAnswer({
                sectionId: candidateDetailsSectionId,
                id: stateQuestion.id,
                question: { ...stateQuestion, answer: stateQuestion.answer?.toString()?.trim() }
            });
        }

        const question = questions.find(q => q.id === questionId);

        if (question) {
            question.answer = question.answer?.toString()?.trim();
        }
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.questions.find(question => question.id === id) && this.props.questions.find(question => question.id === id).answer;
    }

    private canSupplyEvidence(id: string) {
        return this.getAnswerToQuestionWithId(id) === "I have evidence I can upload";
    }

    private getManagersAnswerToQuestionWithId(id: string) {
        return this.props.managersSectionQuestions.find(question => question.id === id)
            && this.props.managersSectionQuestions.find(question => question.id === id).answer;
    }

    private getQuestionConfig(questionId: string) {
        return this.props.questionnaireConfig.questions.find(c => c.id === questionId) || { id: questionId, disabled: true };
    }

    private shouldHighlightYesNoAnswer = (answer: QuestionAnswer) => this.props.highlightSpecificAnswers && answer === "Yes";

    private openBmiModal() {
        this.setState({ isBmiModalOpen: true });
    }

    private closeBmiModal() {
        this.setState({ isBmiModalOpen: false });
    }

    private calculateMetricBmi(metricHeight: number, metricWeight: number) {
        const heightInCms = metricHeight * 100;
        const bmi = calculateBmiService({ weightInKgs: metricWeight, heightInCms });

        this.setState({ calculatedBmi: bmi });

        this.props.updateAnswer({
            sectionId: "Covid19RiskAssessment",
            id: questionIds.Bmi,
            question: { question: "Calculated BMI", answer: bmi, isValid: true }
        });
    }

    private calculateImperialBmi(heightFeet: number, heightInches: number, weightStone: number, weightPounds: number) {
        const heightInInches = heightFeet * 12 + heightInches;
        const heightInMeters = heightInInches / 39.37;
        const weightInPounds = weightStone * 14 + weightPounds;
        const weightInKgs = weightInPounds / 2.205;

        this.calculateMetricBmi(heightInMeters, weightInKgs);
    }

    private calculateBmi() {
        if (this.state.bmiModalActiveIndex === 0) {
            this.calculateMetricBmi(this.state.metricHeight, this.state.metricWeight);
            return;
        }

        this.calculateImperialBmi(
            this.state.imperialHeightFeet,
            this.state.imperialHeightInches,
            this.state.imperialWeightStone,
            this.state.imperialWeightPounds);
    }

    private handleTabChange(event: React.MouseEvent<HTMLDivElement, MouseEvent>, data: TabProps) {
        this.setState({ bmiModalActiveIndex: Number(data.activeIndex) });
    }

    private handleMetricHeightChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        this.setState({ metricHeight: Number(data.value) });
    }

    private handleMetricWeightChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        this.setState({ metricWeight: Number(data.value) });
    }

    private handleImperialHeightFeetChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        this.setState({ imperialHeightFeet: Number(data.value) });
    }

    private handleImperialHeightInchesChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        this.setState({ imperialHeightInches: Number(data.value) });
    }

    private handleImperialWeightStoneChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        this.setState({ imperialWeightStone: Number(data.value) });
    }

    private handleImperialWeightPoundsChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        this.setState({ imperialWeightPounds: Number(data.value) });
    }
}

const mapStateToProps = (state: {
    referralForm: ReferralFormState;
    questionnaires: QuestionnairesState;
    router: Location;
},
ownProps: OwnProps): StateProps => {
    return {
        caseId: ownProps.caseId || state.router.params.ohCaseId,
        questions: state.questionnaires[QUESTIONNAIRE_KEY] ?
            uniq(flatten(state.questionnaires[QUESTIONNAIRE_KEY].sections.map(section => values(section.questions)))) :
            [],
        managersSectionQuestions: state.questionnaires.PpqManagerSection ?
            uniq(flatten(state.questionnaires.PpqManagerSection.sections.map(section => values(section.questions)))) :
            []
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })),
    removeQuestion: payload => dispatch(removeQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })),
    openFirstSection: () => dispatch(openSection({ questionnaireKey: QUESTIONNAIRE_KEY, sectionId: "CandidateDetails" }))
});

export const PpqEmployeeSection = connect(mapStateToProps, mapDispatchToProps)(PpqEmployeeSectionUnconnected);
